import React, { useState } from "react";

import { Col, Row, Button, Badge } from "reactstrap";

import TransactionsTable from "../TransactionsTable";
import {
  useFundTransactionModal,
  initialState,
} from "../../../../../providers/fundTransactionModalProvider";
import { useFundContext } from "../../../../../providers/fundsProvider";
import FundQuarterCloseModal from "./FundQuarterCloseModal";
import { utils } from "../../../../../utils/utils";
import InformationModal from "../../../../InformationModal";
import moment from "moment";
import ConfirmationModal from "../../../../ConfirmationModal";
import { fundsApi } from "../../../../../services/fundServices";
import { useAuth } from "../../../../../providers/authProvider";
import { CUSTOMER_SUPPORT, SPONSOR } from "../../../../../utils/roles";
import { useNavigate } from "react-router-dom";

const CREATION_MODE = 1;

const STATUS_OPEN = "1";
const STATUS_CLOSED = "2";

const TYPE_INCOME = "1";
const TYPE_INCOME_DND_ID = "3";

const TYPE_QUARTER = "2";

const FundQuarterTransactions = ({ fundQuarter }) => {
  const navigate = useNavigate();
  const [fundContext, setFundContext] = useFundContext();
  const [fundTransactionModal, setFundTransactionModal] =
    useFundTransactionModal();

  const [fundQuarterCloseModal, setFundQuarterCloseModal] = useState(false);

  const [informationModal, setInformationModal] = useState({
    isOpen: false,
    body: null,
  });

  const initConfirmationModal = {
    isOpen: false,
    onSubmit: null,
    onClose: null,
    title: "",
    body: "",
  };

  const [confirmationModal, setConfirmationModal] = useState(
    initConfirmationModal
  );

  const [authContext] = useAuth();

  const user = authContext.currentUser;

  const quarterBalance =
    (fundQuarter.transactions || []).reduce(
      (p, c) =>
        p +
        c.amount *
          (c.type === TYPE_INCOME || c.type === TYPE_INCOME_DND_ID ? 1 : -1),
      0
    ) || 0;

  const onClose = () => {
    if (quarterBalance < 0) {
      return setInformationModal({
        isOpen: true,
        body: "You can't close the quarter with a negative balance. Please check.",
      });
    }
    setFundContext({ selectedFundQuarter: fundQuarter });
    setFundQuarterCloseModal(true);
  };

  const onDistributeFunds = () => {
    setConfirmationModal({
      isOpen: true,
      onSubmit: () => {
        fundsApi.distributeQuarterFunds({ id: fundQuarter.id }).then(() => {
          fundQuarter.fundsDistributed = true;
          let fundQuarters = fundContext.fundQuarters;
          fundQuarters.splice(
            fundQuarters.findIndex((fq) => fq.id === fundQuarter.id),
            1,
            fundQuarter
          );
          setFundContext({ fundQuarters });
          setConfirmationModal(initConfirmationModal);
        });
      },
      onClose: () => setConfirmationModal(initConfirmationModal),
      title: `Distribute Q${fundQuarter.quarter}-${fundQuarter.year} Funds`,
      body: `<p>Investor's Distributions will be generated with the information entered.</p>
      <p class="text-warning">In case they exist, they will be overwritten.</p>
      <p>The Distributions will be available from the Program detail section.</p>
      <span class="font-weight-bold">You confirm you want to continue?</span>`,
    });
  };

  const onReopen = () => {
    setConfirmationModal({
      isOpen: true,
      onSubmit: () => {
        fundsApi.repenFundQuarter({ id: fundQuarter.id }).then(() => {
          fundQuarter.status = STATUS_OPEN;
          fundQuarter.fundsDistributed = false;
          let fundQuarters = fundContext.fundQuarters;
          fundQuarters.splice(
            fundQuarters.findIndex((fq) => fq.id === fundQuarter.id),
            1,
            fundQuarter
          );
          setFundContext({ fundQuarters });
          setConfirmationModal(initConfirmationModal);
        });
      },
      onClose: () => setConfirmationModal(initConfirmationModal),
      title: `Reopen Q${fundQuarter.quarter}-${fundQuarter.year}`,
      body: `<p class="text-warning font-500">In case that the distributions were already generated, they will be removed.</p><span>You confirm you want to re-open the quarter?</span>`,
    });
  };

  const isQuarterBeforeActiveQuarter = (fundQuarter) => {
    const activeQ = fundContext.fundQuarters.find((q) => q.active);
    return moment()
      .set("year", fundQuarter.year)
      .set("Q", fundQuarter.quarter)
      .isSameOrBefore(
        moment().set("year", activeQ.year).set("Q", activeQ.quarter)
      );
  };

  return (
    <Col className="mb-4 mx-0 px-0">
      <Row className="mx-0 mb-3 align-items-center justify-content-between">
        <h5 className="mb-0 d-flex flex-column">
          <span>Q{fundQuarter.quarter} Transactions</span>
          <div>
            <small className="font-size-75">Balance:</small>
            <small
              className={`font-size-75 ml-1 ${
                quarterBalance < 0 ? "text-danger" : "text-success"
              }`}
            >
              {utils.formatCurrency(quarterBalance)}
            </small>
          </div>
        </h5>
        <div className="d-flex align-items-center">
          {fundQuarter.status !== STATUS_OPEN ? (
            <div className="d-flex align-items-center">
              {!utils.userHasRole(user, CUSTOMER_SUPPORT) &&
                !utils.userHasRole(user, SPONSOR) && (
                  <>
                    <Button
                      className="ml-4 flex-shrink-0"
                      color={"warning"}
                      onClick={onReopen}
                    >
                      Re-Open Quarter
                    </Button>
                    {fundQuarter.fundsDistributed ? (
                      <>
                        <Button
                          className="ml-4 flex-shrink-0"
                          color={"tertiary"}
                          onClick={onDistributeFunds}
                        >
                          Re-Distribute Funds
                        </Button>
                        <Button
                          className="ml-4 flex-shrink-0"
                          color={"dark"}
                          onClick={() =>
                            navigate(
                              `/back/funds/${fundQuarter.fundId}/fund-quarters/${fundQuarter.id}/distributions`
                            )
                          }
                        >
                          View Distributions
                        </Button>
                      </>
                    ) : (
                      <Button
                        className="ml-4 flex-shrink-0"
                        color={"tertiary"}
                        onClick={onDistributeFunds}
                      >
                        Distribute Funds
                      </Button>
                    )}
                  </>
                )}
              <Badge color={"danger"} size={18} pill className="ml-4">
                Closed
              </Badge>
            </div>
          ) : (
            <>
              {!utils.userHasRole(user, CUSTOMER_SUPPORT) &&
                !utils.userHasRole(user, SPONSOR) && (
                  <>
                    <Button
                      className="ml-4 flex-shrink-0"
                      color={"secondary"}
                      onClick={() => {
                        setFundContext({ selectedFundQuarter: fundQuarter });
                        setFundTransactionModal({
                          ...initialState,
                          categories: fundTransactionModal.categories,
                          isModalOpen: true,
                          mode: CREATION_MODE,
                        });
                      }}
                    >
                      Add Transaction
                    </Button>
                    {isQuarterBeforeActiveQuarter(fundQuarter) ? (
                      <Button
                        className="ml-4 flex-shrink-0"
                        color={"warning"}
                        onClick={onClose}
                      >
                        Close Quarter
                      </Button>
                    ) : null}
                  </>
                )}
            </>
          )}
          {fundQuarter.active ? (
            <Badge color={"success"} size={18} pill className="ml-4">
              Active
            </Badge>
          ) : null}
        </div>
      </Row>
      <TransactionsTable period={fundQuarter} type={TYPE_QUARTER} />
      {fundQuarterCloseModal ? (
        <FundQuarterCloseModal
          onSubmitted={() => {
            fundQuarter.status = STATUS_CLOSED;
            let fundQuarters = fundContext.fundQuarters;
            fundQuarters.splice(
              fundQuarters.findIndex((fq) => fq.id === fundQuarter.id),
              1,
              fundQuarter
            );
            setFundContext({ fundQuarters, refresh: !fundContext.refresh });
            setFundQuarterCloseModal(false);
          }}
          onClose={() => {
            setFundQuarterCloseModal(false);
          }}
        />
      ) : null}
      {informationModal?.isOpen ? (
        <InformationModal
          title={"Heads Up!"}
          body={informationModal.body}
          onClose={() => setInformationModal({ isOpen: false, body: "" })}
        />
      ) : null}
      {confirmationModal?.isOpen ? (
        <ConfirmationModal {...confirmationModal} />
      ) : null}
    </Col>
  );
};

export default FundQuarterTransactions;
